/**
 * Module 3: What Do You Actually Want to Say?
 *
 * In which Philemon is made to say it in one sentence, and Theron eats the
 * rest.
 * Primary character: Theron | Supporting: Philemon, Xenia
 *
 * REWORKED 2026-10-04 to the six rules of Evening 1
 * (docs/CONTENT_REVIEW_2026-10-04.md).
 *
 *   1. One idea, shown once. Tonight's is: say it in one sentence first.
 *      Philemon tries three times. Nobody states the moral; the line left
 *      standing is Theron's "Interesting is not the same as necessary."
 *   2. Something happens tonight. Theron makes Philemon say what he teaches in
 *      one sentence that the guest could carry home, and lays the argument out
 *      in grapes to do it. It goes badly twice (one breath with a dozen
 *      turnings, then short with nothing in it) before it goes well.
 *   3. Somebody is wrong and stays wrong. Philemon holds that some things will
 *      not go into one sentence. He is half right ("Put Eudoros in one
 *      sentence") and he keeps the half. Theron does not concede either.
 *   4. The reader's turn: Theron asks what kind of thing you have to say this
 *      month, and gives his recipe for that kind.
 *   5. One box in the open (the curse of knowledge, straight after the guest
 *      fails to say Philemon's sentence back). The rest waits in the notes,
 *      and the notes say which parts are the course's own.
 *   6. One exercise in the scene (harvest, cut, sentence), one to take away
 *      (know, feel, do, then try it on a person), and the Message Distillery.
 *
 * CONTINUITY this evening must keep (other evenings depend on it):
 *   - Theron opens: "Two evenings of philosophy. My patience is now spent."
 *     He was not at Evening 2 (he was at Piraeus); Philemon told him about it
 *     on the walk here. Xenia's "the three you gave me last night" are the
 *     three small questions Philemon answered plainly in Evening 2.
 *   - Theron's name for his method is "the distillery", and the must is
 *     boiled down the way the syrup-makers do. Wine is not distilled.
 *   - Xenia names "the other" Carthage story and Theron denies it, as on the
 *     first evening. Evening 7 tells it. Do not tell it here.
 *   - Eudoros is alive (he dies on the morning of Evening 5). Philemon says he
 *     will not go into one sentence; by Evening 8 he has spoken of him to a
 *     full courtyard.
 *   - At the end Xenia announces the gathering: the largest the courtyard has
 *     held, one month from tonight. She asks Philemon to speak, in front of
 *     witnesses so that he cannot decline, and Theron is to prepare one story.
 *     Evening 4 is three weeks out and opens with "the guest Xenia promised",
 *     so the preview must promise her.
 *
 * The one study here (Camerer, Loewenstein and Weber, 1989) was checked on
 * 2026-10-04; see docs/audits/finding-your-voice-2026-10/register-modules-2-3.md.
 * Its wording is the ceiling: shorten it, never strengthen it.
 */

export const MODULE_03_CONTENT = [
  // --- At the gate ---
  {
    type: 'narration',
    text: 'Theron is waiting at the gate, which he has never done. Before you are through it he wants to know what Philemon teaches, and quickly. You are still hunting for an answer when he nods and walks you in. Xenia is at the herb bed. Philemon sits at the low table beside a bowl of grapes, looking content.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Two evenings of philosophy. My patience is now spent.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'You were not here for the second.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'You told me the second on the walk here. All of the walk. And when I ask our guest what you teach, I get a kind look. Thirty years, Philemon. What do you teach? One sentence our guest could carry home.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Gladly. I teach the art of speaking, by which I do not mean ornament, though ornament has its place, but the ordering of an argument, so that a listener, supposing him willing, which he seldom is, may be brought by way of the enthymeme, rightly understood, to...',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Stop. Is an enthymeme a thing you teach or a thing you catch?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'It was one sentence.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'It was one breath. So is a scream.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Could you say it back to us?',
  },
  {
    type: 'narration',
    text: 'She is asking you. You have "the art of speaking". After that the road forked several times, and you lost him at the first.',
  },
  {
    type: 'research',
    title: 'Why you lost him',
    source: 'Camerer, Loewenstein and Weber, Journal of Political Economy, 1989',
    paragraphs: [
      'Once you know something, it is hard to imagine not knowing it. The name for this, the "curse of knowledge", comes from a 1989 paper by three economists: Colin Camerer, George Loewenstein and Martin Weber.',
      'They told business students what real companies had actually earned in a year, then paid them for accurate guesses at what other students, who had not been told, had predicted. The informed students could not set aside what they knew. It is one reason an expert can be hard to follow.',
    ],
  },

  // --- The distillery ---
  {
    type: 'heading',
    text: 'The distillery',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'What I teach will not go into one sentence, and that is no fault in the thing.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'And the three you gave me last night?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'You asked me small things.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Then it is a fault in the packing. In Carthage I sold Athenian pots to a man with three words of Greek.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Is that the Carthage story you tell, or the other one?',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'There is one Carthage story.',
  },
  {
    type: 'narration',
    text: 'He pulls the bowl of grapes across the table.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Here is how I prepare any message. I call it the distillery. You start with a harvest of grapes, and you boil the must down the way the syrup-makers do, until what is left is concentrated and clear. So: the harvest. Everything you could say about what you teach, and a grape for each.',
  },
  {
    type: 'narration',
    text: 'Philemon is good at this part. The history of the art. The ordering of an argument. The voice, the pause. Eudoros. What fear does to a speaker. Theron sets down a grape for each, in a row in front of you, and when Philemon stops for breath there are eleven.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Now the cut.',
  },
  {
    type: 'narration',
    text: 'He eats one.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'That was the history of the art.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'It was very good.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'It was interesting!',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'It was. Interesting is not the same as necessary.',
  },
  {
    type: 'narration',
    text: 'He eats another. Philemon puts his hand over the row.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Philemon. If our guest could carry only three of those out of the gate, which three?',
  },
  {
    type: 'narration',
    text: 'It takes him a long time. He pushes three grapes towards you and sits looking at the other six as if they were students he had failed. Theron eats them.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Three, then. Now one sentence.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'I teach people to speak well.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'So does every fraud with a stool in the agora. Short, and nothing in it. You kept three good grapes and handed our guest the stalk.',
  },
  {
    type: 'narration',
    text: 'Philemon frowns at his grapes. Theron turns to you.',
  },
  {
    type: 'yourTurn',
    id: 'm3-what-cargo',
    character: 'theron',
    speaker: 'Theron',
    prompt: 'While he thinks. You are carrying something as well. Something you must say before the month is out, and have not said. What kind of cargo is it?',
    options: [
      {
        label: 'I have to ask for something.',
        reply: {
          character: 'theron',
          speaker: 'Theron',
          text: 'Then ask in the first breath. "I want this, and here is what you get for it." People bury the asking under the weather and the health of the family. Nobody can say yes to a thing they are still waiting to hear.',
        },
      },
      {
        label: 'I have to give someone bad news.',
        reply: {
          character: 'theron',
          speaker: 'Theron',
          text: 'Bad news goes first and goes plain. "The ship is lost." Then what you will do about it. Do not walk them round the harbour. They saw your face when you came in, and they are already counting.',
        },
      },
      {
        label: 'I have to explain what I do.',
        reply: {
          character: 'theron',
          speaker: 'Theron',
          text: 'Then you are Philemon, and I am sorry. Do not say what it is. Say who is better off for it. "I get your oil to Syracuse unspilt." Nobody has ever asked me for the history of ships.',
        },
      },
      {
        label: 'I have to speak up in a meeting.',
        reply: {
          character: 'theron',
          speaker: 'Theron',
          text: 'Then make your sentence before you go in, not while the others are talking. Say it early: the point first, the reason after. A thing held back gets heavier with every voice that speaks before yours.',
        },
      },
    ],
  },
  {
    type: 'exercise',
    number: 1,
    title: 'One sentence',
    instructions: 'Take the thing you just named to Theron and put it through his distillery. Write it down; it does not work in your head.',
    prompts: [
      'The harvest. Write down everything you could say about it: every point, every reason, every "and another thing". Do not sort it yet.',
      'The cut. Cross out whatever is interesting but not necessary. If your listener could carry only three things away, which three?',
      'The sentence. Say what is left in one sentence, aloud.',
      'Check it as Theron checked Philemon. Is it one breath with a dozen turnings? Is it short, with nothing in it? Then go back to the cut.',
    ],
  },

  // --- Three questions ---
  {
    type: 'heading',
    text: 'Three questions',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Three questions, Philemon. You are saying it to our guest, not to the Lyceum. When you stop, what should our guest know?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'That speaking is a craft. Nobody is born to it. I was not.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'And feel?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Must they feel something?',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'I am not asking after anyone\'s soul. A buyer who feels nothing keeps his purse shut. Feel what?',
  },
  {
    type: 'narration',
    text: 'Philemon looks at you for some time.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'That it is not too late. It was very nearly too late for me.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'And do?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Come back.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Now say it. To our guest.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Speaking is a craft, I learnt it late and badly myself, and if you come back I will teach it to you.',
  },
  {
    type: 'narration',
    text: 'You could say that back. You could say it at the well tomorrow.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'There. I would buy that.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'It leaves out nearly everything.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'That is what is good about it.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'It is a door, Theron. I will not call it the house. Put Eudoros in one sentence.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: '"He taught Philemon to speak."',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Short, and nothing in it.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Then you have not cut enough.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Nor will I.',
  },
  {
    type: 'narration',
    text: 'Theron reaches for one of the three grapes. Philemon moves them.',
  },

  // --- One month ---
  {
    type: 'heading',
    text: 'One month',
  },
  {
    type: 'narration',
    text: 'Something in the way Xenia takes the middle of the courtyard makes even Theron sit down.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'One month from tonight I am opening the gates. The largest gathering this courtyard has held. Philosophers, merchants, strangers.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Magnificent. I will prepare a story or three.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'You will prepare one, Theron. Boiled down, by your own method. And Philemon. I would like you to speak that night.',
  },
  {
    type: 'narration',
    text: 'Nobody reaches for anything. Philemon has not spoken to a full courtyard in a long time.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'You ask me this in front of witnesses, so that I cannot decline gracefully.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'I do. That is exactly what I am doing.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'And what am I to say to them?',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'What do you actually want to say?',
  },
  {
    type: 'narration',
    text: 'He has no sentence for that. Xenia turns to you.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'And you will be there. Sitting where you are sitting now, if that is all you want. But a month is a long time. We may make a speaker of you yet.',
  },
  {
    type: 'narration',
    text: 'The three grapes are still on the table. Theron reaches for one, looks at Philemon\'s face, and takes his hand back.',
  },

  {
    type: 'divider',
  },

  // --- To take away ---
  {
    type: 'heading',
    text: 'Before the next evening',
  },
  {
    type: 'exercise',
    number: 2,
    title: 'Know, feel, do',
    instructions: 'Before you use your sentence, test it twice: against Theron\'s three questions, and on a person.',
    prompts: [
      'Know. When you stop speaking, what one thing should they know?',
      'Feel. What should they feel? Worried, reassured, curious? Choose one.',
      'Do. What should they do next, even if it is only to think again on the way home?',
      'Now say your sentence to someone who does not know the subject, and ask them to say it back. Whatever they lose is what your sentence has not yet said.',
    ],
  },
  {
    type: 'toolLink',
    toolId: 'message-distillery',
    title: 'Message Distillery',
    text: 'Theron\'s tool takes one message through five steps: the harvest, the cut, the sentence, the three questions and three anchor words. It counts how much you have cut and keeps what you write.',
  },

  // --- The research, for whoever wants it ---
  {
    type: 'notes',
    title: 'What we know now',
    intro: 'Theron can only tell you what thirty years of trade taught him. One study sits behind tonight. The other two notes are the course\'s own, and say so.',
    items: [
      {
        title: 'No neat cure',
        paragraphs: [
          'Experts tend to skip steps, reach for jargon, and assume background knowledge their audience doesn\'t have. This need not be arrogance. They underestimate how hard the thing looks to someone meeting it for the first time.',
          'The curse of knowledge has no neat cure. In the 1989 experiments that gave it its name, neither payment nor feedback removed it. The practical defence is to put yourself, deliberately, in the position of someone hearing your message for the first time and, better still, to try it on someone who is. What do they need to know first? What can you safely leave out? What are you assuming?',
        ],
        source: 'Camerer, Loewenstein and Weber, Journal of Political Economy, 1989',
      },
      {
        title: 'Hedging',
        paragraphs: [
          'Our own observation. Notice how many qualifiers you use when you speak: "sort of", "kind of", "maybe", "I think", "just my opinion". Stacked up, they water down your message and make you sound less sure than you are.',
          'Before the cut: "So I\'ve been thinking about this for a while and there are a few angles to consider, and I know some people might disagree, but I think, and this is just my perspective, that maybe we should consider, you know, possibly changing the way we approach the quarterly reviews." After it: "Our quarterly reviews aren\'t working. I\'d like to propose a specific change: replace the written report with a 15-minute conversation."',
          'Hedging feels like humility, but it often works as self-protection. Dropping it does not mean speaking with false certainty. Where you are unsure, say so honestly. Drop the hedges that are only there to protect you.',
        ],
      },
      {
        title: 'What is ours, and not a finding',
        paragraphs: [
          'The one-sentence test is the course\'s own rule of thumb. Complex ideas are not simple, and Philemon is right about that. The test asks you to find the heart of the idea, and to decide what to leave out, before you open your mouth.',
          'Know, feel, do is a planning habit we use, not a research finding. It separates three things speakers tend to muddle together. You might be clear on what you want people to know, yet never have considered what you want them to feel. Or you might want them to act without ever building the feeling that motivates action.',
          'The three answers also make an honest test afterwards: did they leave knowing it, feeling it and doing it? If not, the gap tells you which of the three to rebuild.',
        ],
      },
    ],
  },

  // --- Module Summary ---
  {
    type: 'summary',
    characterId: 'theron',
    points: [
      'Harvest everything, cut what is only interesting, and say what is left in one sentence.',
      'Test the sentence with three questions: what should they know, feel and do?',
      'Once you know something it is hard to imagine not knowing it, so try your sentence on someone who doesn\'t.',
    ],
    nextPreview: {
      dialogue: [
        {
          character: 'xenia',
          text: 'Come early next time. A guest is joining us: someone who has just found out, rather suddenly, that she has to speak. You will like her. She reminds me of you.',
        },
        {
          character: 'theron',
          text: 'I will bring more grapes.',
        },
      ],
    },
  },
];

export const MODULE_03_EXERCISES = [
  {
    number: 1,
    title: 'One sentence',
    description: 'Take one thing you have to say through the harvest, the cut and the sentence.',
    xpReward: 20,
  },
  {
    number: 2,
    title: 'Know, feel, do',
    description: 'Test your sentence against three questions, then on a person.',
    xpReward: 20,
  },
];

export const MODULE_03_SUMMARY = {
  title: 'What Do You Actually Want to Say?',
  keyInsight: 'Say it in one sentence before you say it at length.',
  characterQuote: {
    character: 'theron',
    text: 'Interesting is not the same as necessary.',
  },
  takeaways: [
    'Harvest everything, cut what is only interesting, and say what is left in one sentence.',
    'Test the sentence with three questions: what should they know, feel and do?',
    'Try your sentence on someone who does not know the subject.',
  ],
};
